import { autorun } from "mobx";
import {
  NewPaymentRelatedSource,
  PaymentMethodPriority,
  PaymentMethodType,
} from "../enums"; 
import { PaymentBankDModel, PaymentCardModel } from "./PaymentMethodModels";
import { MerchantAccount } from "./MerchantApplicationInputModel";
import { ContactReferenceModel } from "./ContactDetailPageModel";
import { CoreInputModelUtil, getNewErrors, InputErrorModel } from "@mindmemobile/mm-shared-global";
import { AuthService } from "../services";
import { IAPIPaymentMethodModel } from "../interfaces/IAPIRelatedPaymentMethod";
import { IAPICreatePaymentMethodModel, IAPIUpdatePaymentMethodModel } from "../interfaces/IAPIUpsertPaymentMethodModels";
import { IAPIGetPaymentMethodSummaryModel } from "../interfaces/IAPIPaymentMethodModels";

export class UpsertPaymentMethodModel {
    id: string;
    isDefault: boolean;
    card?: PaymentCardModel | null;
    bank?: PaymentBankDModel | null;
    type: keyof typeof PaymentMethodType;
    merchantAccount?: MerchantAccount;
    contact?: ContactReferenceModel;
    contactId: string;
    paymentMethodId: string;
    priority: keyof typeof PaymentMethodPriority;
    showLoader: boolean;
    source: keyof typeof NewPaymentRelatedSource;
    sourceId: string; //id of contact, subscription, invoice, checkout, purchaselinks
    errors: InputErrorModel<UpsertPaymentMethodModel>[];

    constructor(data?: Partial<UpsertPaymentMethodModel>) {
        this.id = data?.id || '';
        this.isDefault = data?.isDefault || false;
        this.card = data?.card ? new PaymentCardModel(data?.card) : new PaymentCardModel();
        this.type = data?.type || PaymentMethodType.Card;
        this.contactId = data?.contactId || '';
        this.merchantAccount = data?.merchantAccount && new MerchantAccount(data.merchantAccount);
        this.contact = data?.contact && new ContactReferenceModel(data.contact);
        this.paymentMethodId = data?.paymentMethodId || '';
        this.priority = data?.priority || PaymentMethodPriority.None;
        this.showLoader = data?.showLoader || false;
        this.source = data?.source || 'Manual';
        this.sourceId = data?.sourceId || '';
        this.bank = data?.bank && new PaymentBankDModel(data.bank);
        this.errors =
            (data &&
                data.errors?.map((e) => {
                    return new InputErrorModel<UpsertPaymentMethodModel>(e);
                })) ||
            [];

        autorun(() => {
            this.validate(true);
        });
    }

    hasError(field: keyof UpsertPaymentMethodModel): boolean {
        return this.errors.some((e) => e.field === field);
    }

    validate(isAutoRun = false): boolean {
        const newErrors: InputErrorModel<UpsertPaymentMethodModel>[] = [];
        if (!this.id) {
            CoreInputModelUtil.validateTextField(this.paymentMethodId, 'paymentMethodId', newErrors);
        }
        this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
        return this.errors.length === 0;
    }

    static deserializeAPIModel(data: IAPIPaymentMethodModel): UpsertPaymentMethodModel {
        const mappedData: Partial<UpsertPaymentMethodModel> = {
            id: data.id,
            isDefault: data.isDefault,
            type: data.type as keyof typeof PaymentMethodType,
            card: data.card && PaymentCardModel.deserializeAPIModel(data.card),
            priority: data.priority as keyof typeof PaymentMethodPriority,
            contactId: data.contactId,
        };
        return new UpsertPaymentMethodModel(mappedData);
    }

    createNewPaymentMethodAPIModel(): IAPICreatePaymentMethodModel {
        return {
            accountId: AuthService.authData.accountId,
            contactId: this.contact?.id || '',
            contact: this.contact?.createAPIModel?.() || null,
            merchantAccount: this.merchantAccount?.createAPIModel2() || ({} as any),
            paymentMethodId: this.paymentMethodId,
            priority: this.priority,
        };
    }

    createUpdateModel(): IAPIUpdatePaymentMethodModel {
        return {
            id: this.id,
            accountId: AuthService.authData.accountId,
            priority: this.priority,
        };
    }
}

export class UpsertPaymentMethodSummaryModel {
    paymentMethodsCount: number;
    hasPrimary: boolean;
    hasBackup: boolean;
    showLoader: boolean;
    backUpMethodsCount: number;
    constructor(data?: Partial<UpsertPaymentMethodSummaryModel>) {
        this.paymentMethodsCount = data?.paymentMethodsCount || 0;
        this.hasPrimary = data?.hasPrimary || false;
        this.hasBackup = data?.hasBackup || false;
        this.showLoader = data?.showLoader || false;
        this.backUpMethodsCount = data?.backUpMethodsCount || 0;
    }

    static deserializeAPIModel(data: IAPIGetPaymentMethodSummaryModel): UpsertPaymentMethodSummaryModel {
        const mappedData: Partial<UpsertPaymentMethodSummaryModel> = {
            paymentMethodsCount: data.paymentMethodCount,
            hasPrimary: data.hasPrimary,
            hasBackup: data.hasBackup,
            showLoader: false,
            backUpMethodsCount: data.backUpMethodsCount,
        };
        return new UpsertPaymentMethodSummaryModel(mappedData);
    }
}
